<template>
  <div>
    <div
      v-if="!editing && ['view', 'both'].includes(method)"
      class="px-2 py-1 bg-gray-200 text-gray-700 rounded-lg cursor-pointer flex justify-between items-center min-h-[28px]"
      @click="method == 'both' ? startEditMode($event) : null"
      @dblclick="method == 'both' ? startEditMode($event) : null"
    >
      <span
        v-if="newValue && customField.field_type === 'multiple_options'"
        class="flex justify-start items-center gap-1"
      >
        <span v-for="(option, index) in newValue" :key="index" class="badge badge-default">
          {{ option }}
        </span>
      </span>
      <div
        v-else-if="customField.field_type === 'rich_text'"
        class="user-generated-content text-sm mt-1 break-words whitespace-pre-line w-full flex-1 min-w-0"
        v-html="sanitize(newValue)"
      ></div>
      <span v-else class="flex items-center jusitfy-between gap-1">
        {{ visibleValue }}&nbsp;
        <a v-if="isUrl" :href="newValue" target="_blank" rel="noopener noreferrer">
          <vue-feather type="external-link" size="16" />
        </a>
      </span>

      <button v-if="customField.sensitive" class="flex items-center gap-1" @click="getSensitive">
        <vue-feather v-if="secureValue" type="eye" size="16" />
        <vue-feather v-else type="eye-off" size="16" />
      </button>
    </div>
    <div v-else-if="['edit', 'both'].includes(method)" ref="customFieldWrapper" class="w-full custom-field-editor">
      <FormKit type="form" :actions="false" @submit="saveCustomFieldValue">
        <QuillInput
          v-if="customField.field_type === 'rich_text'"
          v-model="newValue"
          :name="customField.name"
          :label="customField.name"
          :label-sr-only="true"
          @blur="saveCustomFieldValue"
        />
        <AddressInput
          v-else-if="customField.field_type === 'address'"
          v-model="newValue"
          :hide-label="true"
          @update:model-value="saveCustomFieldValue"
        />
        <FormKit
          v-else-if="customFieldInputType === 'date'"
          ref="customFieldInput"
          v-model="newValue"
          type="date"
          value-format="iso"
          @input="saveDate"
        />
        <FormKit
          v-else-if="customField.field_type === 'multiple_options'"
          ref="customFieldInput"
          v-model="newValue"
          type="checkbox"
          :options="customFieldOptions"
          @input="trackMultipleOptionsChange"
        />
        <FormKit
          v-else
          ref="customFieldInput"
          v-model="newValue"
          :type="customFieldInputType"
          :options="customFieldOptions"
          @keydown.enter="saveCustomFieldValue"
        />
        <FormKit
          v-if="displaySubmitButton && !['rich_text', 'address'].includes(customField.field_type)"
          type="submit"
          name="Save"
        />
      </FormKit>
    </div>
    <Modal
      v-if="showDelete"
      ref="modal"
      title="Delete custom field"
      body="Are you sure you want to delete this custom field? Deleting a custom field is permanent."
      action="Delete"
      action-color="red"
      @close-modal="showDelete = false"
      @handle-action="deleteCustomFieldValue"
    />
  </div>
</template>

<script>
import Modal from '../shared/Modal.vue';
import { formatStandardDate, formatCurrencyToHuman } from '../../utilities/format.ts';
import QuillInput from '../shared/QuillInput.vue';
import { sanitizeRichText } from '../../utilities/sanitization';
import AddressInput from '../shared/AddressInput.vue';

export default {
  components: {
    Modal,
    QuillInput,
    AddressInput,
  },
  props: {
    customField: {
      type: Object,
      required: true,
    },
    customFieldValue: {
      type: Object,
      required: false,
    },
    customFieldableType: {
      type: String,
      required: true,
    },
    customFieldableId: {
      type: [Number, String],
      required: true,
    },
    method: {
      type: String,
      required: false,
    },
    displaySubmitButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      editing: false,
      newValue: '',
      secureValue: null,
      showDelete: false,
      initialValue: '',
    };
  },
  computed: {
    currentValue() {
      return this.newValue;
    },
    customFieldInputType() {
      switch (this.customField.field_type) {
        case 'short_text':
          return 'text';
        case 'single_option':
          return 'select';
        case 'multiple_options':
          return 'checkbox';
        case 'number':
          return 'number';
        case 'percentage':
          return 'number';
        case 'currency':
          return 'number';
        case 'date':
          return 'date';
        case 'address':
          return 'address';
        default:
          return 'text';
      }
    },
    customFieldOptions() {
      if (!this.customField.options || this.customField.options.length === 0) return [];
      return this.customField.options.map(option => {
        return { label: option, value: option };
      });
    },
    visibleValue() {
      if (this.customField.sensitive && !this.customFieldValue && !this.newValue) {
        return '';
      } else if (this.customField.sensitive && !this.secureValue) {
        return '**************';
      } else if (this.customField && this.customField.sensitive && this.secureValue) {
        return this.secureValue;
      } else if (this.customField.field_type === 'date' && this.newValue) {
        return formatStandardDate(this.newValue);
      } else if (this.customField.field_type === 'currency' && this.newValue) {
        return formatCurrencyToHuman(this.newValue, 2);
      } else if (this.customField.field_type === 'percentage' && this.newValue) {
        return `${this.newValue}%`;
      } else {
        return this.newValue;
      }
    },
    isUrl() {
      const regex =
        /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
      return regex.test(this.newValue);
    },
  },
  watch: {
    newValue: {
      handler(newVal) {
        this.$emit('updated', {
          custom_field_value: {
            key: this.customField.name,
            custom_fieldable_type: this.customFieldableType,
            custom_fieldable_id: this.customFieldableId,
            custom_field_id: this.customField.id,
            value:
              this.customField.field_type === 'multiple_options' && Array.isArray(newVal)
                ? newVal.length
                  ? newVal.join(',')
                  : ''
                : [newVal].flat().join(','), // support checkbox array values
          },
        });
      },
    },
  },
  mounted() {
    this.newValue = this.customFieldValue?.value || '';
    this.initialValue = this.newValue; // Store initial value

    if (this.customFieldValue?.value && this.customField.field_type === 'multiple_options') {
      this.newValue = this.customFieldValue.value.split(',');
      this.initialValue = [...this.newValue]; // Clone the array for comparison
    }

    if (this.customFieldValue?.value && this.customField.field_type === 'date') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(this.newValue)) {
        const dateObj = new Date(this.newValue);
        if (!isNaN(dateObj.getTime())) {
          this.newValue = dateObj.toISOString().split('T')[0];
          this.initialValue = this.newValue;
        }
      }
    }
  },
  methods: {
    async saveDate(e) {
      this.newValue = e;
    },
    trackMultipleOptionsChange(e) {
      this.newValue = e || [];
      this.saveCustomFieldValue();
    },
    async getSensitive(e) {
      e.stopPropagation();
      if (!this.customFieldValue) return;

      if (this.secureValue) {
        this.secureValue = null;
        return;
      }

      const res = await fetch(`/api/custom_field_values/${this.customFieldValue.id}/decrypt`);
      const { record } = await res.json();

      this.secureValue = record.secure_value;
    },
    async startEditMode(e) {
      if (this.customField.sensitive) {
        this.secureValue = null;
        await this.getSensitive(e);
        this.newValue = this.secureValue;
      }

      if (this.customField.field_type === 'single_option' && this.newValue === '') {
        this.newValue = this.customField.options[0];
      }

      this.editing = true;

      this.$nextTick(() => {
        if (this.$refs.customFieldInput) {
          this.$refs.customFieldInput.$el.querySelector('input')?.focus();
        }
      });
    },
    async saveCustomFieldValue() {
      if (this.method == 'both') {
        this.editing = false;
        this.secureValue = null;

        const res = await fetch('/api/custom_field_values', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
          },
          body: JSON.stringify({
            custom_field_value: {
              custom_fieldable_type: this.customFieldableType,
              custom_fieldable_id: this.customFieldableId,
              custom_field_id: this.customField.id,
              value: [this.newValue].flat().join(','), // support checkbox array values
            },
          }),
        });
        const data = await res.json();

        if (data.status == 'error') {
          this.$store.commit('general/showAlert', {
            alertType: 'error',
            alertTitle: 'Custom Field Update Error',
            alertDescription: data.message,
          });
        }

        this.initialValue = Array.isArray(this.newValue) ? [...this.newValue] : this.newValue;

        this.$emit('updated', {
          custom_field_value: {
            key: this.customField.name,
            custom_fieldable_type: this.customFieldableType,
            custom_fieldable_id: this.customFieldableId,
            custom_field_id: this.customField.id,
            value: [this.newValue].flat().join(','),
          },
        });
      }
    },
    async deleteCustomFieldValue() {
      if (this.method == 'both') {
        this.showDelete = false;

        const res = await fetch(`/api/custom_field_values/${this.customFieldValue.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
          },
        });
        this.$emit('updated');
        this.$store.commit('general/showAlert', {
          alertType: 'success',
          alertTitle: 'Custom Field Deleted',
          alertDescription: 'Your custom field was successfully deleted.',
        });
      }
    },
    sanitize(html) {
      return sanitizeRichText(html);
    },
    formatStandardDate,
  },
};
</script>
